import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { ThankYou } from "./ThankYou";
import { submitLead, captureTracking } from "../../lib/api";
import { SERVICES, SCHOOL_TYPES, STUDENT_STRENGTHS, CALL_TIMES } from "../../data/services";
import { REQUIREMENT_OPTIONS } from "../../data/requirements";
import { LEAD_FORM } from "../../constants/testIds";

const STEPS = [
	{ title: "Contact Details", subtitle: "Where should we send your growth strategy?" },
	{ title: "School Profile", subtitle: "Tell us about your institution" },
	{ title: "Growth Goals", subtitle: "Select your immediate priorities" },
	{ title: "Target & Consent", subtitle: "Final details to customize your plan" },
];

const emptyForm = {
	full_name: "", phone: "", email: "",
	school_name: "", city: "", state: "", school_type: "",
	requirement: "", current_student_strength: "",
	expected_admission_target: "", message: "", preferred_call_time: "",
	marketing_consent: false, company_website: "",
};

function validIndianPhone(p) {
	let d = (p || "").replace(/\D/g, "");
	if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
	if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
	return /^[6-9]\d{9}$/.test(d);
}
function validEmail(e) {
	return !e || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
}

export function MultiStepLeadForm({ sourcePage = "Homepage", defaultRequirement = "", onDone, onExplore, embedded = false }) {
	const [step, setStep] = useState(0);
	const [dir, setDir] = useState(1);
	const [form, setForm] = useState({ ...emptyForm, requirement: defaultRequirement });
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);
	const [leadId, setLeadId] = useState("");

	const set = (k, v) => {
		setForm((f) => ({ ...f, [k]: v }));
		setErrors((e) => ({ ...e, [k]: undefined }));
	};

	const validateStep = () => {
		const e = {};
		if (step === 0) {
			if (!form.full_name.trim()) e.full_name = "Please enter your name";
			if (!form.phone.trim()) e.phone = "Phone number is required";
			else if (!validIndianPhone(form.phone)) e.phone = "Enter a valid 10-digit Indian mobile number";
			if (!validEmail(form.email)) e.email = "Enter a valid email address";
		}
		if (step === 1) {
			if (!form.school_name.trim()) e.school_name = "School name is required";
			if (!form.city.trim()) e.city = "City is required";
		}
		if (step === 2) {
			if (!form.requirement) e.requirement = "Please select what you need help with";
		}
		if (step === 3) {
			if (!form.marketing_consent) e.marketing_consent = "Please accept to be contacted";
		}
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const next = () => {
		if (!validateStep()) return;
		setDir(1);
		setStep((s) => Math.min(s + 1, 3));
	};
	const prev = () => {
		setDir(-1);
		setStep((s) => Math.max(s - 1, 0));
	};

	const handleSubmit = async () => {
		if (!validateStep()) return;
		setSubmitting(true);
		try {
			const tracking = captureTracking(sourcePage);
			const res = await submitLead({ ...form, ...tracking });
			setLeadId(res.lead_id || "");
			setDone(true);
		} catch (err) {
			if (err?.response?.status === 429) {
				toast.error("Too many attempts. Please wait a moment and try again.");
			} else if (err?.response?.status === 422) {
				const be = err.response.data?.detail?.errors || {};
				setErrors(be);
				toast.error("Please check the highlighted fields.");
				const firstStep = be.full_name || be.phone || be.email ? 0
					: be.school_name || be.city ? 1
					: be.requirement ? 2 : 3;
				setStep(firstStep);
			} else {
				toast.error("Something went wrong. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	};

	const reset = () => {
		setForm({ ...emptyForm, requirement: defaultRequirement });
		setStep(0);
		setDone(false);
		setLeadId("");
	};

	if (done) {
		return (
			<ThankYou
				leadId={leadId}
				compact={embedded}
				onExplore={onExplore}
				onClose={() => { reset(); onDone && onDone(); }}
			/>
		);
	}

	const progress = ((step + 1) / 4) * 100;

	return (
		<div data-testid={LEAD_FORM.form} className="w-full">
			{/* progress */}
			<div className="mb-6">
				<div className="flex items-center justify-between mb-2">
					<span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-600 dark:text-emerald-400">
						{STEPS[step].title}
					</span>
					<span data-testid={LEAD_FORM.progress} className="font-mono-sx text-xs text-slate-500 dark:text-slate-400">
						{step + 1} / 4
					</span>
				</div>
				<div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
					<motion.div
						className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
						initial={false}
						animate={{ width: `${progress}%` }}
						transition={{ duration: 0.4 }}
					/>
				</div>
				<p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{STEPS[step].subtitle}</p>
			</div>

			{/* honeypot (hidden from humans) */}
			<input
				type="text"
				tabIndex={-1}
				autoComplete="off"
				data-testid={LEAD_FORM.honeypot}
				value={form.company_website}
				onChange={(e) => set("company_website", e.target.value)}
				className="absolute left-[-9999px] h-0 w-0 opacity-0"
				aria-hidden="true"
			/>

			<AnimatePresence mode="wait" custom={dir}>
				<motion.div
					key={step}
					custom={dir}
					initial={{ opacity: 0, x: dir * 40 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: dir * -40 }}
					transition={{ duration: 0.28 }}
				>
					{step === 0 && (
						<div data-testid={LEAD_FORM.step(1)} className="space-y-4">
							<Field label="Full Name" error={errors.full_name} required>
								<Input data-testid={LEAD_FORM.fullName} placeholder="e.g. Rajesh Sharma"
									value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
							</Field>
							<Field label="Phone Number" error={errors.phone} required>
								<Input data-testid={LEAD_FORM.phone} type="tel" placeholder="10-digit mobile number"
									value={form.phone} onChange={(e) => set("phone", e.target.value)} />
							</Field>
							<Field label="Email Address" error={errors.email}>
								<Input data-testid={LEAD_FORM.email} type="email" placeholder="you@school.com"
									value={form.email} onChange={(e) => set("email", e.target.value)} />
							</Field>
						</div>
					)}

					{step === 1 && (
						<div data-testid={LEAD_FORM.step(2)} className="space-y-4">
							<Field label="School Name" error={errors.school_name} required>
								<Input data-testid={LEAD_FORM.schoolName} placeholder="e.g. Sunrise Public School"
									value={form.school_name} onChange={(e) => set("school_name", e.target.value)} />
							</Field>
							<div className="grid grid-cols-2 gap-3">
								<Field label="City" error={errors.city} required>
									<Input data-testid={LEAD_FORM.city} placeholder="City"
										value={form.city} onChange={(e) => set("city", e.target.value)} />
								</Field>
								<Field label="State" error={errors.state}>
									<Input data-testid={LEAD_FORM.state} placeholder="State"
										value={form.state} onChange={(e) => set("state", e.target.value)} />
								</Field>
							</div>
							<Field label="School Type" error={errors.school_type}>
								<Select value={form.school_type} onValueChange={(v) => set("school_type", v)}>
									<SelectTrigger data-testid={LEAD_FORM.schoolType}>
										<SelectValue placeholder="Select school type" />
									</SelectTrigger>
									<SelectContent>
										{SCHOOL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
									</SelectContent>
								</Select>
							</Field>
						</div>
					)}

					{step === 2 && (
						<div data-testid={LEAD_FORM.step(3)} className="space-y-4">
							<Field label="What do you need help with?" error={errors.requirement} required>
								<Select value={form.requirement} onValueChange={(v) => set("requirement", v)}>
									<SelectTrigger data-testid={LEAD_FORM.requirement}>
										<SelectValue placeholder="Select your requirement" />
									</SelectTrigger>
									<SelectContent>
										{REQUIREMENT_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
									</SelectContent>
								</Select>
							</Field>
							<Field label="Current Student Strength" error={errors.current_student_strength}>
								<Select value={form.current_student_strength} onValueChange={(v) => set("current_student_strength", v)}>
									<SelectTrigger data-testid={LEAD_FORM.studentStrength}>
										<SelectValue placeholder="Select strength" />
									</SelectTrigger>
									<SelectContent>
										{STUDENT_STRENGTHS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
									</SelectContent>
								</Select>
							</Field>
						</div>
					)}

					{step === 3 && (
						<div data-testid={LEAD_FORM.step(4)} className="space-y-4">
							<div className="grid grid-cols-2 gap-3">
								<Field label="Expected Admission Target" error={errors.expected_admission_target}>
									<Input data-testid={LEAD_FORM.admissionTarget} placeholder="e.g. 200"
										value={form.expected_admission_target} onChange={(e) => set("expected_admission_target", e.target.value)} />
								</Field>
								<Field label="Preferred Call Time" error={errors.preferred_call_time}>
									<Select value={form.preferred_call_time} onValueChange={(v) => set("preferred_call_time", v)}>
										<SelectTrigger data-testid={LEAD_FORM.callTime}>
											<SelectValue placeholder="Anytime" />
										</SelectTrigger>
										<SelectContent>
											{CALL_TIMES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
										</SelectContent>
									</Select>
								</Field>
							</div>
							<Field label="Message" error={errors.message}>
								<Textarea data-testid={LEAD_FORM.message} rows={3} placeholder="Anything you'd like us to know?"
									value={form.message} onChange={(e) => set("message", e.target.value)} />
							</Field>
							<label className="flex items-start gap-3 cursor-pointer rounded-lg border border-slate-200 dark:border-slate-700 p-3">
								<Checkbox data-testid={LEAD_FORM.consent} checked={form.marketing_consent}
									onCheckedChange={(v) => set("marketing_consent", !!v)} className="mt-0.5" />
								<span className="text-sm text-slate-600 dark:text-slate-300">
									I agree to be contacted by SchoolXpert360 regarding my enquiry.
								</span>
							</label>
							{errors.marketing_consent && (
								<p className="text-sm text-red-500 -mt-2">{errors.marketing_consent}</p>
							)}
						</div>
					)}
				</motion.div>
			</AnimatePresence>

			<div className="mt-8 flex items-center gap-3">
				{step > 0 && (
					<Button variant="outline" onClick={prev} data-testid={LEAD_FORM.prev} className="rounded-full">
						<ArrowLeft className="mr-1 h-4 w-4" /> Back
					</Button>
				)}
				{step < 3 ? (
					<Button onClick={next} data-testid={LEAD_FORM.next}
						className="ml-auto rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-6">
						Continue <ArrowRight className="ml-1 h-4 w-4" />
					</Button>
				) : (
					<Button onClick={handleSubmit} disabled={submitting} data-testid={LEAD_FORM.submit}
						className="ml-auto rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-6 font-semibold">
						{submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
							: <>GET MY FREE CONSULTATION</>}
					</Button>
				)}
			</div>

			<div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
				<ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Secure & confidential. No spam, ever.
			</div>
		</div>
	);
}

function Field({ label, error, required, children }) {
	return (
		<div>
			<Label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
				{label} {required && <span className="text-emerald-600">*</span>}
			</Label>
			{children}
			{error && <p className="mt-1 text-sm text-red-500">{error}</p>}
		</div>
	);
}
